import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import LineChart from '@cloudscape-design/components/line-chart';
import type { DailyPostHog } from '@etiquetachef/types';
import { ChartCard } from '@/features/dashboard-finance/charts/ChartCard';
import { formatInt, formatYyyymmddDisplay } from '@/features/dashboard-finance/formatters';

/**
 * Série DAU/WAU/MAU do PostHog; dia com valor `null` (Query API falhou) é
 * desenhado como 0 — achado registrado na #926.
 */
export function DauWauMauChart({
  series,
}: {
  series: readonly DailyPostHog[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const x = (d: DailyPostHog): string => formatYyyymmddDisplay(d.yyyymmdd);
  const dau = series.map((d) => ({ x: x(d), y: d.dau ?? 0 }));
  const wau = series.map((d) => ({ x: x(d), y: d.wau ?? 0 }));
  const mau = series.map((d) => ({ x: x(d), y: d.mau ?? 0 }));
  const valueFormatter = (v: number): string => formatInt(v);

  return (
    <ChartCard title={t('product.charts.dauWauMau')}>
      <LineChart
        series={[
          { title: 'DAU', type: 'line', data: dau, color: '#2563eb', valueFormatter },
          { title: 'WAU', type: 'line', data: wau, color: '#10b981', valueFormatter },
          { title: 'MAU', type: 'line', data: mau, color: '#f97316', valueFormatter },
        ]}
        xScaleType="categorical"
        height={230}
        hideFilter
        ariaLabel={t('product.charts.dauWauMau')}
        i18nStrings={{ yTickFormatter: valueFormatter }}
        empty={<Box textAlign="center" color="inherit">{t('finance.empty')}</Box>}
      />
    </ChartCard>
  );
}
